import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, AlertCircle, Home, RefreshCw, XCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AcademySuccess = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [registrationData, setRegistrationData] = useState(null);
  const [membershipNumbers, setMembershipNumbers] = useState([]);
  const [pollAttempts, setPollAttempts] = useState(0);

  const urlParams = new URLSearchParams(window.location.search);
  const sessionId = urlParams.get('session_id');
  const registrationId = urlParams.get('registration_id');

  const maxPollAttempts = 10;
  const pollInterval = 2000;

  const fetchRegistrationDetails = useCallback(async () => {
    if (!registrationId) return;
    try {
      const response = await fetch(`${API_URL}/api/academy/registration/${registrationId}`);
      const data = await response.json();
      if (response.ok) setRegistrationData(data);
    } catch (err) {
      console.error('[Fetch Registration Error]', err);
    }
  }, [registrationId]);

  const checkPaymentStatus = useCallback(async () => {
    if (!sessionId || !registrationId) {
      setError('Missing payment information. Please contact support.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/academy/payment-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, registration_id: registrationId }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to check payment status');

      if (data.payment_status === 'paid' || data.registration_status === 'confirmed') {
        setPaymentStatus('success');
        setLoading(false);
        if (data.membership_numbers?.length > 0) setMembershipNumbers(data.membership_numbers);
        fetchRegistrationDetails();
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setLoading(false);
      } else {
        setPollAttempts((prev) => prev + 1);
      }
    } catch (err) {
      console.error('[Payment Check Error]', err);
      setError(err.message || 'Failed to verify payment');
      setLoading(false);
    }
  }, [sessionId, registrationId, fetchRegistrationDetails]);

  useEffect(() => {
    if (pollAttempts === 0) checkPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading && pollAttempts > 0 && pollAttempts < maxPollAttempts) {
      const timer = setTimeout(checkPaymentStatus, pollInterval);
      return () => clearTimeout(timer);
    } else if (pollAttempts >= maxPollAttempts && loading) {
      setPaymentStatus('pending');
      setLoading(false);
      fetchRegistrationDetails();
    }
  }, [pollAttempts, loading, checkPaymentStatus, fetchRegistrationDetails]);

  const handleRetry = () => {
    setLoading(true); setError(''); setPollAttempts(0); checkPaymentStatus();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="academy-success-loading">
        <Card className="bg-gradient-card gold-border max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 text-primary animate-spin mx-auto mb-4" />
            <h2 className="font-display text-xl text-primary mb-2">Verifying Payment</h2>
            <p className="text-muted-foreground text-sm">Please wait while we confirm your payment...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="academy-success-error">
        <Card className="bg-gradient-card gold-border max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
            <h2 className="font-display text-xl text-destructive mb-2">Verification Error</h2>
            <p className="text-muted-foreground text-sm mb-4">{error}</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={handleRetry} variant="outline"><RefreshCw className="h-4 w-4 mr-2" />Try Again</Button>
              <Button onClick={() => window.location.href = '/'}><Home className="h-4 w-4 mr-2" />Home</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentStatus === 'expired') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="bg-gradient-card gold-border max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <XCircle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
            <h2 className="font-display text-xl text-yellow-500 mb-2">Payment Expired</h2>
            <p className="text-muted-foreground text-sm mb-4">Your payment session has expired.</p>
            <Button onClick={() => window.location.href = '/register-academy'}>Register Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-8" data-testid="academy-success-confirmed">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full mx-4">
        <Card className="bg-gradient-card gold-border">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-10 w-10 text-success mx-auto mb-4" />
            <h2 className="font-display text-2xl text-primary mb-2">
              {paymentStatus === 'success' ? 'Payment Successful!' : 'Registration Received!'}
            </h2>
            <p className="text-muted-foreground mb-6">
              {paymentStatus === 'success'
                ? 'Your academy registration has been confirmed.'
                : 'Your payment is being processed. You will receive confirmation shortly.'}
            </p>

            <div className="bg-charcoal rounded-lg p-4 mb-6 text-left space-y-3">
              <div>
                <p className="text-xs text-muted-foreground">Registration ID</p>
                <p className="font-mono text-primary" data-testid="success-registration-id">{registrationId}</p>
              </div>

              {membershipNumbers.length > 0 && (
                <div className="p-3 bg-green-900/20 border border-green-600/30 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Academy Membership Number(s)</p>
                  {membershipNumbers.map((num, idx) => (
                    <p key={idx} className="font-mono text-green-400 font-bold" data-testid={`membership-number-${idx}`}>{num}</p>
                  ))}
                </div>
              )}

              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <Badge className={paymentStatus === 'success' ? 'bg-success' : 'bg-yellow-600'} data-testid="success-status-badge">
                  {paymentStatus === 'success' ? 'Confirmed' : 'Processing'}
                </Badge>
              </div>

              {registrationData?.fees?.total && (
                <div>
                  <p className="text-xs text-muted-foreground">Total Paid</p>
                  <p className="text-primary font-bold">${registrationData.fees.total.toFixed(2)} SGD</p>
                </div>
              )}
            </div>

            <Button onClick={() => window.location.href = '/'} className="w-full bg-primary text-primary-foreground" data-testid="success-home-btn">
              <Home className="h-4 w-4 mr-2" /> Back to Home
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AcademySuccess;
